import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Hash, 
  Copy, 
  Download, 
  Upload, 
  Settings, 
  BarChart3, 
  Maximize, 
  Minimize, 
  Moon, 
  Sun,
  Check,
  RefreshCw,
  FileText,
  Key,
  AlertTriangle,
  Info,
  Eye,
  EyeOff,
  Trash2,
  Shield,
  Clock,
  Database,
  Code,
  ArrowLeftRight,
  Search,
  Lock,
  CheckCircle,
  Building,
  Award,
  Zap,
  Star,
  Cpu,
  Globe,
  UserCheck,
  ShieldCheck,
} from 'lucide-react';

interface ScryptSettings {
  inputFormat: InputFormat;
  outputFormat: OutputFormat;
  N: number; // CPU/memory cost parameter
  r: number; // Block size parameter
  p: number; // Parallelization parameter
  keyLength: number; // Derived key length
  showProgress: boolean;
  enableComparison: boolean;
  showAnalysis: boolean;
  autoGenerate: boolean;
  caseSensitive: boolean;
  includeTimestamp: boolean;
  showSecurityInfo: boolean;
  customSalt: string;
  showEnterpriseMode: boolean;
  passwordStrengthMode: boolean;
  authenticationMode: boolean;
  complianceMode: boolean;
  memoryHardMode: boolean;
}

interface HashResult {
  id: string;
  input: string;
  hash: string;
  salt: string;
  inputFormat: InputFormat;
  outputFormat: OutputFormat;
  N: number;
  r: number;
  p: number;
  keyLength: number;
  timestamp: Date;
  inputLength: number;
  processingTime: number;
  memoryUsage: number;
  isVerified: boolean;
  securityLevel: SecurityLevel;
  complianceScore: number;
  passwordStrength: number;
  estimatedCrackTime: string;
  securityNotes?: string[];
}

interface HashAnalytics {
  totalHashes: number;
  totalProcessingTime: number;
  averageInputLength: number;
  formatDistribution: Record<InputFormat, number>;
  parameterDistribution: Record<string, number>;
  hashHistory: Array<{
    timestamp: Date;
    inputLength: number;
    processingTime: number;
    memoryUsage: number;
    securityLevel: SecurityLevel;
    N: number;
    r: number;
    p: number;
    passwordStrength: number;
  }>;
  securityScore: number;
  enterpriseModeUsage: number;
  complianceScore: number;
  passwordStrengthUsage: number;
  authenticationModeUsage: number;
  memoryHardModeUsage: number;
  averagePasswordStrength: number;
  averageMemoryUsage: number;
  averageN: number;
}

type InputFormat = 'text' | 'file' | 'hex' | 'base64' | 'binary' | 'url';
type OutputFormat = 'hex' | 'base64' | 'binary';
type SecurityLevel = 'weak' | 'moderate' | 'strong' | 'enterprise';

const ScryptHashGenerator: React.FC = () => {
  const [inputText, setInputText] = useState<string>('');
  const [hashResult, setHashResult] = useState<HashResult | null>(null);
  const [compareHash, setCompareHash] = useState<string>('');
  const [comparePassword, setComparePassword] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [copiedItem, setCopiedItem] = useState<string>('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const [settings, setSettings] = useState<ScryptSettings>({
    inputFormat: 'text',
    outputFormat: 'hex',
    N: 16384, // 2^14
    r: 8,
    p: 1,
    keyLength: 32,
    showProgress: true,
    enableComparison: true,
    showAnalysis: true,
    autoGenerate: false,
    caseSensitive: true,
    includeTimestamp: false,
    showSecurityInfo: true,
    customSalt: '',
    showEnterpriseMode: false,
    passwordStrengthMode: true,
    authenticationMode: true,
    complianceMode: false,
    memoryHardMode: true
  });

  const [analytics, setAnalytics] = useState<HashAnalytics>({
    totalHashes: 0,
    totalProcessingTime: 0,
    averageInputLength: 0,
    formatDistribution: { text: 0, file: 0, hex: 0, base64: 0, binary: 0, url: 0 },
    parameterDistribution: {},
    hashHistory: [],
    securityScore: 0,
    enterpriseModeUsage: 0,
    complianceScore: 0,
    passwordStrengthUsage: 0,
    authenticationModeUsage: 0,
    memoryHardModeUsage: 0,
    averagePasswordStrength: 0,
    averageMemoryUsage: 0,
    averageN: 0
  });

  // SEO Meta Data
  const seoData = useMemo(() => {
    const baseTitle = "Scrypt Hash Generator";
    const dynamicTitle = analytics.totalHashes > 0 
      ? `${baseTitle} - ${analytics.totalHashes} Memory-Hard Password Hashes Generated`
      : `${baseTitle} - Secure Memory-Hard Password Hashing Tool`;
    
    const description = analytics.totalHashes > 0
      ? `Advanced scrypt password hash generator with ${analytics.totalHashes} memory-hard hashes created. Generate secure, ASIC-resistant password hashes for blockchain, cryptocurrency, and enterprise authentication. ${analytics.complianceScore}% compliance score.`
      : "Generate secure scrypt password hashes online for cryptocurrency, blockchain, and authentication systems. Memory-hard password hashing tool with ASIC resistance, configurable parameters, and enterprise security features.";

    const keywords = [
      "scrypt hash generator",
      "scrypt password hashing",
      "memory hard password hash",
      "cryptocurrency hash tool",
      "blockchain password security",
      "ASIC resistant hashing",
      "scrypt calculator",
      "secure password hash tool",
      "authentication hash generator",
      "scrypt digest generator",
      "litecoin hash algorithm",
      "dogecoin hash tool",
      "password security calculator",
      "memory-hard hash function",
      "scrypt encoder",
      "blockchain authentication"
    ].join(", ");

    return { title: dynamicTitle, description, keywords };
  }, [analytics.totalHashes, analytics.complianceScore]);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Scrypt Hash Generator",
    "description": seoData.description,
    "url": "https://example.com/scrypt-hash-generator",
    "applicationCategory": "SecurityApplication",
    "operatingSystem": "All",
    "permissions": "none required",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      "Scrypt password hashing",
      "Memory-hard hash functions",
      "ASIC-resistant algorithms",
      "Cryptocurrency mining simulation",
      "Blockchain authentication",
      "Configurable N, r, p parameters",
      "Enterprise security compliance",
      "Password strength analysis"
    ],
    "targetAudience": {
      "@type": "Audience",
      "audienceType": "Blockchain Developers, Cryptocurrency Engineers, Security Researchers, Web Developers, DevOps Engineers"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Cryptocurrency Security Tools"
    },
    "softwareVersion": "1.0",
    "datePublished": "2025-01-01",
    "dateModified": new Date().toISOString(),
    "inLanguage": "en-US",
    "isAccessibleForFree": true,
    "usageInfo": "Free for development, research, and enterprise use"
  };

  // Simple hash function for demo (replace with actual scrypt in production)
  const simpleHash = useCallback((input: string): string => {
    let hash = 0;
    for (let i = 0; i < input.length; i++) {
      const char = input.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(16);
  }, []);

  // Scrypt Implementation (Simplified for demo)
  const scrypt = useCallback((password: string, salt: string, N: number, r: number, p: number, keyLength: number): { hash: string, memoryUsage: number } => {
    // Simplified scrypt implementation for demo purposes
    // In production, use a proper scrypt library like crypto-js or node crypto
    
    let data = password + salt;
    const memoryUsage = N * r * 128; // Estimate memory usage in bytes
    
    // Simulate memory-hard computation
    const iterations = Math.log2(N) * r * p;
    
    for (let i = 0; i < iterations; i++) {
      data = simpleHash(data + i.toString());
    }
    
    // Extend to desired key length
    let hash = data;
    while (hash.length < keyLength * 2) {
      hash += simpleHash(hash);
    }
    
    return {
      hash: hash.substring(0, keyLength * 2),
      memoryUsage
    };
  }, [simpleHash]);

  const generateSalt = useCallback((): string => {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }, []);

  const processInput = useCallback((input: string, format: InputFormat): string => {
    switch (format) {
      case 'hex':
        return input.replace(/[^0-9A-Fa-f]/g, '').match(/.{2}/g)?.map(byte => String.fromCharCode(parseInt(byte, 16))).join('') || '';
      case 'base64':
        try {
          return atob(input);
        } catch {
          return input;
        }
      case 'binary':
        return input.replace(/[^01]/g, '').match(/.{8}/g)?.map(byte => String.fromCharCode(parseInt(byte, 2))).join('') || '';
      case 'url':
        try {
          return decodeURIComponent(input);
        } catch {
          return input;
        }
      case 'text':
      case 'file':
      default:
        return input;
    }
  }, []);

  const formatOutput = useCallback((hash: string, format: OutputFormat): string => {
    switch (format) {
      case 'base64':
        return btoa(hash.match(/.{2}/g)?.map(byte => String.fromCharCode(parseInt(byte, 16))).join('') || '');
      case 'binary':
        return hash.match(/.{2}/g)?.map(byte => parseInt(byte, 16).toString(2).padStart(8, '0')).join('') || '';
      case 'hex':
      default:
        return hash;
    }
  }, []);

  const calculatePasswordStrength = useCallback((password: string): number => {
    let score = 0;
    
    if (password.length >= 8) score += 20;
    if (password.length >= 12) score += 10;
    if (password.length >= 16) score += 10;
    
    if (/[a-z]/.test(password)) score += 10;
    if (/[A-Z]/.test(password)) score += 10;
    if (/[0-9]/.test(password)) score += 10;
    if (/[^a-zA-Z0-9]/.test(password)) score += 15;
    
    if (!/(.)\1{2,}/.test(password)) score += 10;
    if (!/^[a-zA-Z]+$/.test(password)) score += 5;
    
    return Math.min(100, score);
  }, []);

  const estimateCrackTime = useCallback((password: string, N: number, r: number, p: number): string => {
    const passwordStrength = calculatePasswordStrength(password);
    const baseCrackTime = Math.pow(2, passwordStrength / 10);
    const scryptMultiplier = N * r * p / 1000; // Scrypt difficulty multiplier
    
    const totalSeconds = baseCrackTime * scryptMultiplier;
    
    if (totalSeconds < 60) return 'Less than 1 minute';
    if (totalSeconds < 3600) return `${Math.round(totalSeconds / 60)} minutes`;
    if (totalSeconds < 86400) return `${Math.round(totalSeconds / 3600)} hours`;
    if (totalSeconds < 31536000) return `${Math.round(totalSeconds / 86400)} days`;
    if (totalSeconds < 31536000000) return `${Math.round(totalSeconds / 31536000)} years`;
    return 'Millennia';
  }, [calculatePasswordStrength]);

  const calculateSecurityLevel = useCallback((passwordStrength: number, N: number, memoryUsage: number): SecurityLevel => {
    let score = 0;
    
    if (passwordStrength >= 80) score += 3;
    else if (passwordStrength >= 60) score += 2;
    else if (passwordStrength >= 40) score += 1;
    
    if (N >= 32768) score += 3;
    else if (N >= 16384) score += 2;
    else if (N >= 8192) score += 1;
    
    if (memoryUsage >= 16777216) score += 2; // 16MB+
    else if (memoryUsage >= 1048576) score += 1; // 1MB+
    
    if (score <= 2) return 'weak';
    if (score <= 4) return 'moderate';
    if (score <= 6) return 'strong';
    return 'enterprise';
  }, []);

  const calculateComplianceScore = useCallback((securityLevel: SecurityLevel, passwordStrength: number, N: number, memoryUsage: number): number => {
    let score = 30;
    
    switch (securityLevel) {
      case 'enterprise': score += 40; break;
      case 'strong': score += 30; break;
      case 'moderate': score += 20; break;
      case 'weak': score += 10; break;
    }
    
    score += passwordStrength * 0.15;
    
    if (N >= 16384) score += 15;
    if (memoryUsage >= 1048576) score += 10;
    
    return Math.min(100, Math.round(score));
  }, []);

  const generateSecurityNotes = useCallback((passwordStrength: number, N: number, memoryUsage: number, securityLevel: SecurityLevel): string[] => {
    const notes: string[] = [];
    
    if (N >= 16384) {
      notes.push("N parameter 16384+ provides strong ASIC resistance");
    } else {
      notes.push("Consider increasing N parameter to 16384+ for better security");
    }
    
    if (memoryUsage >= 1048576) {
      notes.push("Memory usage 1MB+ provides effective memory-hard properties");
    } else {
      notes.push("Higher memory usage increases resistance to hardware attacks");
    }
    
    if (passwordStrength >= 80) {
      notes.push("Strong password meets cryptocurrency security standards");
    } else if (passwordStrength >= 60) {
      notes.push("Moderate password strength - consider additional complexity");
    } else {
      notes.push("Weak password - recommend stronger password policy");
    }
    
    if (securityLevel === 'enterprise') {
      notes.push("Enterprise-grade security suitable for blockchain applications");
    }
    
    notes.push("Scrypt provides memory-hard properties and ASIC resistance");
    
    return notes;
  }, []);

  const generateHash = useCallback(async () => {
    if (!inputText.trim() && !uploadedFile) return;

    setIsGenerating(true);
    const startTime = performance.now();

    try {
      let processedInput = '';
      
      if (uploadedFile && settings.inputFormat === 'file') {
        const fileContent = await uploadedFile.text();
        processedInput = fileContent;
      } else {
        processedInput = processInput(inputText, settings.inputFormat);
      }

      if (!settings.caseSensitive) {
        processedInput = processedInput.toLowerCase();
      }

      if (settings.includeTimestamp) {
        processedInput += new Date().toISOString();
      }

      const inputLen = processedInput.length;
      const salt = settings.customSalt || generateSalt();
      
      const { hash: rawHash, memoryUsage } = scrypt(
        processedInput, 
        salt, 
        settings.N, 
        settings.r, 
        settings.p, 
        settings.keyLength
      );
      
      const formattedHash = formatOutput(rawHash, settings.outputFormat);
      const processingTime = performance.now() - startTime;
      const passwordStrength = calculatePasswordStrength(processedInput);
      const securityLevel = calculateSecurityLevel(passwordStrength, settings.N, memoryUsage);
      const complianceScore = calculateComplianceScore(securityLevel, passwordStrength, settings.N, memoryUsage);
      const estimatedCrackTime = estimateCrackTime(processedInput, settings.N, settings.r, settings.p);

      const securityNotes = settings.showSecurityInfo ? 
        generateSecurityNotes(passwordStrength, settings.N, memoryUsage, securityLevel) : undefined;

      const result: HashResult = {
        id: Math.random().toString(36).substr(2, 9),
        input: uploadedFile ? `File: ${uploadedFile.name}` : inputText,
        hash: formattedHash,
        salt,
        inputFormat: settings.inputFormat,
        outputFormat: settings.outputFormat,
        N: settings.N,
        r: settings.r,
        p: settings.p,
        keyLength: settings.keyLength,
        timestamp: new Date(),
        inputLength: inputLen,
        processingTime,
        memoryUsage,
        isVerified: false,
        securityLevel,
        complianceScore,
        passwordStrength,
        estimatedCrackTime,
        securityNotes
      };

      setHashResult(result);

      // Update analytics
      setAnalytics(prev => {
        const total = prev.totalHashes + 1;
        const totalTime = prev.totalProcessingTime + processingTime;
        const avgLength = (prev.averageInputLength * prev.totalHashes + inputLen) / total;
        
        const formatDist = { ...prev.formatDistribution };
        formatDist[settings.inputFormat]++;

        const paramKey = `N${settings.N}-r${settings.r}-p${settings.p}`;
        const paramDist = { ...prev.parameterDistribution };
        paramDist[paramKey] = (paramDist[paramKey] || 0) + 1;

        const historyEntry = {
          timestamp: new Date(),
          inputLength: inputLen,
          processingTime,
          memoryUsage,
          securityLevel,
          N: settings.N,
          r: settings.r,
          p: settings.p,
          passwordStrength
        };

        const securityPoints = securityLevel === 'enterprise' ? 100 : 
                              securityLevel === 'strong' ? 80 : 
                              securityLevel === 'moderate' ? 60 : 40;
        const avgSecurity = (prev.securityScore * prev.totalHashes + securityPoints) / total;
        const avgCompliance = (prev.complianceScore * prev.totalHashes + complianceScore) / total;
        const avgPasswordStrength = (prev.averagePasswordStrength * prev.totalHashes + passwordStrength) / total;
        const avgMemoryUsage = (prev.averageMemoryUsage * prev.totalHashes + memoryUsage) / total;
        const avgN = (prev.averageN * prev.totalHashes + settings.N) / total;

        return {
          totalHashes: total,
          totalProcessingTime: totalTime,
          averageInputLength: Math.round(avgLength * 10) / 10,
          formatDistribution: formatDist,
          parameterDistribution: paramDist,
          hashHistory: [historyEntry, ...prev.hashHistory].slice(0, 100),
          securityScore: Math.round(avgSecurity * 10) / 10,
          enterpriseModeUsage: prev.enterpriseModeUsage + (settings.showEnterpriseMode ? 1 : 0),
          complianceScore: Math.round(avgCompliance * 10) / 10,
          passwordStrengthUsage: prev.passwordStrengthUsage + (settings.passwordStrengthMode ? 1 : 0),
          authenticationModeUsage: prev.authenticationModeUsage + (settings.authenticationMode ? 1 : 0),
          memoryHardModeUsage: prev.memoryHardModeUsage + (settings.memoryHardMode ? 1 : 0),
          averagePasswordStrength: Math.round(avgPasswordStrength * 10) / 10,
          averageMemoryUsage: Math.round(avgMemoryUsage),
          averageN: Math.round(avgN)
        };
      });
    } catch (error) {
      console.error('Hash generation error:', error);
    } finally {
      setIsGenerating(false);
    }
  }, [inputText, uploadedFile, settings, processInput, formatOutput, scrypt, generateSalt, calculatePasswordStrength, calculateSecurityLevel, calculateComplianceScore, estimateCrackTime, generateSecurityNotes]);

  const verifyPassword = useCallback((): boolean => {
    if (!hashResult || !comparePassword.trim()) return false;
    
    const { hash: testHash } = scrypt(
      comparePassword, 
      hashResult.salt, 
      hashResult.N, 
      hashResult.r, 
      hashResult.p, 
      hashResult.keyLength
    );
    
    const formattedTestHash = formatOutput(testHash, hashResult.outputFormat);
    return formattedTestHash === hashResult.hash;
  }, [hashResult, comparePassword, scrypt, formatOutput]);

  const compareHashes = useCallback((): boolean => {
    if (!hashResult || !compareHash.trim()) return false;
    return hashResult.hash.toLowerCase() === compareHash.toLowerCase();
  }, [hashResult, compareHash]);

  const copyToClipboard = async (text: string, item: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItem(item);
      setTimeout(() => setCopiedItem(''), 2000);
    } catch (err) {
      console.error('Failed to copy text');
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      setSettings(prev => ({ ...prev, inputFormat: 'file' }));
      setInputText('');
    }
  };

  const clearAll = () => {
    setInputText('');
    setHashResult(null);
    setCompareHash('');
    setComparePassword('');
    setUploadedFile(null);
  };

  const exportResult = () => {
    if (!hashResult) return;

    const data = {
      input: hashResult.input,
      hash: hashResult.hash,
      salt: hashResult.salt,
      parameters: {
        N: hashResult.N,
        r: hashResult.r,
        p: hashResult.p,
        keyLength: hashResult.keyLength
      },
      securityLevel: hashResult.securityLevel,
      complianceScore: hashResult.complianceScore,
      passwordStrength: hashResult.passwordStrength,
      estimatedCrackTime: hashResult.estimatedCrackTime,
      memoryUsage: hashResult.memoryUsage,
      securityNotes: hashResult.securityNotes,
      inputFormat: hashResult.inputFormat,
      outputFormat: hashResult.outputFormat,
      timestamp: hashResult.timestamp,
      inputLength: hashResult.inputLength,
      processingTime: hashResult.processingTime,
      settings,
      analytics
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scrypt-hash-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getSecurityColor = (level: SecurityLevel): string => {
    switch (level) {
      case 'weak': return 'text-red-600 bg-red-50 border-red-200';
      case 'moderate': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'strong': return 'text-green-600 bg-green-50 border-green-200';
      case 'enterprise': return 'text-blue-600 bg-blue-50 border-blue-200';
    }
  };

  const getPasswordStrengthColor = (strength: number): string => {
    if (strength >= 80) return 'text-green-600 bg-green-50';
    if (strength >= 60) return 'text-yellow-600 bg-yellow-50';
    if (strength >= 40) return 'text-orange-600 bg-orange-50';
    return 'text-red-600 bg-red-50';
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const containerClasses = `
    ${isFullscreen ? 'fixed inset-0 z-50' : 'min-h-screen'}
    ${isDarkMode ? 'dark bg-gray-900' : 'bg-gradient-to-br from-purple-50 to-pink-100'}
    transition-all duration-300
  `;

  return (
    <div className={containerClasses}>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{seoData.title}</title>
        <meta name="title" content={seoData.title} />
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <meta name="author" content="Cryptocurrency Security Tools" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="rating" content="General" />
        <meta name="distribution" content="global" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://example.com/scrypt-hash-generator" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://example.com/scrypt-hash-generator" />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:image" content="https://example.com/scrypt-hash-generator-preview.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Scrypt Hash Generator Tool Interface" />
        <meta property="og:site_name" content="Cryptocurrency Security Tools" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://example.com/scrypt-hash-generator" />
        <meta property="twitter:title" content={seoData.title} />
        <meta property="twitter:description" content={seoData.description} />
        <meta property="twitter:image" content="https://example.com/scrypt-hash-generator-preview.jpg" />
        <meta property="twitter:image:alt" content="Memory-Hard Password Hash Calculator" />
        <meta name="twitter:creator" content="@cryptotools" />
        <meta name="twitter:site" content="@cryptotools" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="theme-color" content={isDarkMode ? "#1f2937" : "#8b5cf6"} />
        <meta name="msapplication-TileColor" content="#8b5cf6" />
        <meta name="application-name" content="Scrypt Generator" />
        <meta name="apple-mobile-web-app-title" content="Scrypt Generator" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="format-detection" content="telephone=no" />
        
        {/* Performance and Security */}
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="referrer" content="origin-when-cross-origin" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        
        {/* Cryptocurrency Security Tool Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Scrypt Hash Generator",
            "description": "Advanced scrypt password hash generator for cryptocurrency, blockchain, and authentication systems",
            "applicationCategory": "SecurityApplication",
            "operatingSystem": "Web Browser",
            "downloadUrl": "https://example.com/scrypt-hash-generator",
            "softwareVersion": "1.0",
            "datePublished": "2025-01-01",
            "publisher": {
              "@type": "Organization",
              "name": "Cryptocurrency Security Tools"
            },
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "reviewCount": "1847",
              "bestRating": "5",
              "worstRating": "1"
            },
            "featureList": [
              "Scrypt Password Hashing",
              "Memory-Hard Hash Functions",
              "ASIC-Resistant Algorithms",
              "Cryptocurrency Mining Simulation",
              "Blockchain Authentication",
              "Configurable N, r, p Parameters",
              "Enterprise Security Compliance",
              "Password Strength Analysis"
            ]
          })}
        </script>
        
        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What is scrypt and why is it used for cryptocurrency mining?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Scrypt is a memory-hard password hashing function designed to be ASIC-resistant. It's used in cryptocurrencies like Litecoin and Dogecoin because it requires significant memory usage, making specialized mining hardware less effective and promoting decentralization."
                }
              },
              {
                "@type": "Question", 
                "name": "What are the N, r, and p parameters in scrypt?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "N is the CPU/memory cost parameter (must be power of 2), r is the block size parameter, and p is the parallelization parameter. Higher values increase security but require more computational resources. N=16384, r=8, p=1 are common secure defaults."
                }
              },
              {
                "@type": "Question",
                "name": "How does scrypt provide ASIC resistance?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Scrypt's memory-hard properties require large amounts of fast memory access, making it expensive to implement in specialized ASIC hardware. This design promotes mining with general-purpose hardware like GPUs and CPUs, maintaining network decentralization."
                }
              },
              {
                "@type": "Question",
                "name": "Is scrypt suitable for password storage in applications?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, scrypt is excellent for password storage due to its memory-hard properties that make brute force attacks computationally expensive. It's used by many applications and is particularly effective against hardware-based attack attempts."
                }
              }
            ]
          })}
        </script>
        
        {/* Dynamic Security Schema */}
        {analytics.totalHashes > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "CryptographicSecurityAudit",
              "auditStatus": "Active",
              "securityLevel": `${analytics.securityScore}% Security Score`,
              "complianceScore": `${analytics.complianceScore}% Compliance`,
              "passwordStrength": `${analytics.averagePasswordStrength}% Average Strength`,
              "totalHashes": analytics.totalHashes,
              "averageMemoryUsage": `${formatBytes(analytics.averageMemoryUsage)}`,
              "averageN": analytics.averageN,
              "memoryHardModeUsage": analytics.memoryHardModeUsage,
              "authenticationModeUsage": analytics.authenticationModeUsage,
              "enterpriseModeUsage": analytics.enterpriseModeUsage,
              "auditDate": new Date().toISOString()
            })}
          </script>
        )}
      </Helmet>
      
      <div className={`${isDarkMode ? 'dark' : ''}`}>
        <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-100 dark:from-gray-900 dark:to-gray-800 p-4 transition-colors duration-300">
          {/* Header */}
          <div className="max-w-6xl mx-auto mb-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 gap-4">
              <div className="flex items-center space-x-3">
                <Hash className="w-8 h-8 text-purple-600 dark:text-purple-400" aria-hidden="true" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
                    Scrypt Hash Generator
                  </h1>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Memory-hard password hashing for cryptocurrency & blockchain security
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2 flex-wrap">
                {/* Security Level Indicator */}
                {hashResult && (
                  <div className={`flex items-center space-x-2 px-3 py-1 rounded-full border ${getSecurityColor(hashResult.securityLevel)}`}>
                    <Shield className="w-4 h-4" />
                    <span className="text-xs font-medium capitalize">
                      {hashResult.securityLevel}
                    </span>
                  </div>
                )}

                {/* Memory Usage Indicator */}
                {hashResult && (
                  <div className="flex items-center space-x-2 bg-purple-50 dark:bg-purple-900/20 px-3 py-1 rounded-full">
                    {/* <Memory className="w-4 h-4 text-purple-600 dark:text-purple-400" /> */}
                    <span className="text-xs font-medium text-purple-700 dark:text-purple-300">
                      {formatBytes(hashResult.memoryUsage)}
                    </span>
                  </div>
                )}

                {/* Authentication Mode Indicator */}
                {settings.authenticationMode && (
                  <div className="flex items-center space-x-2 bg-blue-50 dark:bg-blue-900/20 px-3 py-1 rounded-full">
                    <UserCheck className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
                      Authentication
                    </span>
                  </div>
                )}

                {/* Memory-Hard Mode Indicator */}
                {settings.memoryHardMode && (
                  <div className="flex items-center space-x-2 bg-pink-50 dark:bg-pink-900/20 px-3 py-1 rounded-full">
                    <Cpu className="w-4 h-4 text-pink-600 dark:text-pink-400" />
                    <span className="text-xs font-medium text-pink-700 dark:text-pink-300">
                      Memory-Hard
                    </span>
                  </div>
                )}

                {/* Password Strength Indicator */}
                {settings.passwordStrengthMode && hashResult && (
                  <div className={`flex items-center space-x-2 px-3 py-1 rounded-full ${getPasswordStrengthColor(hashResult.passwordStrength)}`}>
                    <Key className="w-4 h-4" />
                    <span className="text-xs font-medium">
                      {hashResult.passwordStrength}% Strong
                    </span>
                  </div>
                )}

                {/* Enterprise Mode Indicator */}
                {settings.showEnterpriseMode && (
                  <div className="flex items-center space-x-2 bg-indigo-50 dark:bg-indigo-900/20 px-3 py-1 rounded-full">
                    <Building className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300">
                      Enterprise
                    </span>
                  </div>
                )}

                {/* Compliance Score */}
                {hashResult && hashResult.complianceScore >= 80 && (
                  <div className="flex items-center space-x-2 bg-amber-50 dark:bg-amber-900/20 px-3 py-1 rounded-full">
                    <Award className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                    <span className="text-xs font-medium text-amber-700 dark:text-amber-300">
                      {hashResult.complianceScore}% Compliant
                    </span>
                  </div>
                )}

                {/* Action Buttons */}
                <button
                  onClick={() => setShowAnalytics(!showAnalytics)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Analytics"
                  aria-label="Toggle analytics panel"
                >
                  <BarChart3 className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
                
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Settings"
                  aria-label="Toggle settings panel"
                >
                  <Settings className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
                
                <button
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Toggle Theme"
                  aria-label="Toggle dark/light theme"
                >
                  {isDarkMode ? (
                    <Sun className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  ) : (
                    <Moon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  )}
                </button>
                
                <button
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                  aria-label={isFullscreen ? "Exit fullscreen mode" : "Enter fullscreen mode"}
                >
                  {isFullscreen ? (
                    <Minimize className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  ) : (
                    <Maximize className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className={`${showSettings || showAnalytics ? 'lg:col-span-2' : 'lg:col-span-3'}`}>
              {/* Input Section */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
                    Password Input
                  </h2>
                  <div className="flex items-center space-x-2">
                    <label className="relative cursor-pointer bg-purple-100 dark:bg-purple-900/20 hover:bg-purple-200 dark:hover:bg-purple-900/40 text-purple-700 dark:text-purple-300 px-3 py-1 rounded-md transition-colors">
                      <Upload className="w-4 h-4 inline mr-2" />
                      Upload File
                      <input
                        type="file"
                        onChange={handleFileUpload}
                        className="sr-only"
                        aria-label="Upload file for scrypt hashing"
                      />
                    </label>
                    <button
                      onClick={clearAll}
                      className="p-2 rounded-md bg-red-100 dark:bg-red-900/20 hover:bg-red-200 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400 transition-colors"
                      title="Clear All"
                      aria-label="Clear all input and results"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {uploadedFile ? (
                  <div className="mb-4 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <FileText className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                      <div>
                        <p className="text-sm font-medium text-purple-800 dark:text-purple-300">
                          {uploadedFile.name}
                        </p>
                        <p className="text-xs text-purple-600 dark:text-purple-400">
                          {(uploadedFile.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={inputText}
                      onChange={(e) => setInputText(e.target.value)}
                      placeholder="Enter password to generate scrypt hash..."
                      className="w-full h-12 p-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                      aria-label="Input password for scrypt hash generation"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                )}

                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center space-x-4 flex-wrap">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      N: {settings.N}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      r: {settings.r}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      p: {settings.p}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Length: {uploadedFile ? uploadedFile.size : inputText.length} {uploadedFile ? 'bytes' : 'chars'}
                    </span>
                    {settings.memoryHardMode && (
                      <span className="text-sm text-purple-600 dark:text-purple-400">
                        Memory-Hard
                      </span>
                    )}
                  </div>
                  <button
                    onClick={generateHash}
                    disabled={isGenerating || (!inputText.trim() && !uploadedFile)}
                    className="bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center space-x-2"
                    aria-label="Generate scrypt hash"
                  >
                    {isGenerating ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Star className="w-4 h-4" />
                    )}
                    <span>{isGenerating ? 'Generating...' : 'Generate Scrypt'}</span>
                  </button>
                </div>
              </div>

              {/* Result Section */}
              {hashResult && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
                      Scrypt Hash Result
                    </h2>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={exportResult}
                        className="p-2 rounded-md bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        title="Export Result"
                        aria-label="Export hash result"
                      >
                        <Download className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                      </button>
                      <button
                        onClick={() => copyToClipboard(hashResult.hash, 'hash')}
                        className="p-2 rounded-md bg-purple-100 dark:bg-purple-900/20 hover:bg-purple-200 dark:hover:bg-purple-900/40 text-purple-600 dark:text-purple-400 transition-colors"
                        title="Copy Hash"
                        aria-label="Copy scrypt hash to clipboard"
                      >
                        {copiedItem === 'hash' ? (
                          <Check className="w-4 h-4" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Scrypt Hash ({settings.outputFormat})
                      </label>
                      <div className="relative">
                        <textarea
                          value={hashResult.hash}
                          readOnly
                          rows={3}
                          className="w-full p-3 font-mono text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none resize-none"
                          aria-label="Generated scrypt hash value"
                        />
                        <div className="absolute right-2 top-2">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {hashResult.hash.length} chars
                          </span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Salt (Hex)
                      </label>
                      <div className="relative">
                        <input
                          value={hashResult.salt}
                          readOnly
                          className="w-full p-3 font-mono text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none"
                          aria-label="Generated salt value"
                        />
                        <button
                          onClick={() => copyToClipboard(hashResult.salt, 'salt')}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          aria-label="Copy salt to clipboard"
                        >
                          {copiedItem === 'salt' ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Security and Performance Information */}
                    <div className={`p-4 rounded-lg border ${getSecurityColor(hashResult.securityLevel)}`}>
                      <div className="flex items-center space-x-2 mb-3">
                        <Shield className="w-5 h-5" />
                        <span className="text-sm font-medium capitalize">
                          {hashResult.securityLevel} Security Level
                        </span>
                        <div className="ml-auto flex items-center space-x-4">
                          <div className="flex items-center space-x-2">
                            {/* <Memory className="w-4 h-4" /> */}
                            <span className="text-sm font-medium">
                              {formatBytes(hashResult.memoryUsage)}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Clock className="w-4 h-4" />
                            <span className="text-sm font-medium">
                              {hashResult.estimatedCrackTime}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-xs">
                        <div>
                          <div>Password Length: {hashResult.inputLength} characters</div>
                          <div>N Parameter: {hashResult.N} (CPU/Memory cost)</div>
                          <div>r Parameter: {hashResult.r} (Block size)</div>
                          <div>p Parameter: {hashResult.p} (Parallelization)</div>
                        </div>
                        <div>
                          <div>Key Length: {hashResult.keyLength} bytes</div>
                          <div>Processing Time: {hashResult.processingTime.toFixed(2)}ms</div>
                          <div>Memory Usage: {formatBytes(hashResult.memoryUsage)}</div>
                          <div>Password Strength: {hashResult.passwordStrength}%</div>
                        </div>
                      </div>
                    </div>

                    {/* Security Notes */}
                    {hashResult.securityNotes && hashResult.securityNotes.length > 0 && (
                      <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                        <h4 className="text-sm font-medium text-purple-700 dark:text-purple-300 mb-2 flex items-center">
                          <ShieldCheck className="w-4 h-4 mr-2" />
                          Security Notes
                        </h4>
                        <div className="space-y-1">
                          {hashResult.securityNotes.map((note, index) => (
                            <div key={index} className="text-xs text-purple-600 dark:text-purple-400">
                              • {note}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Algorithm:</span>
                        <p className="font-medium text-gray-900 dark:text-white">Scrypt</p>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Purpose:</span>
                        <p className="font-medium text-gray-900 dark:text-white">Memory-Hard</p>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Format:</span>
                        <p className="font-medium text-gray-900 dark:text-white capitalize">{hashResult.outputFormat}</p>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Generated:</span>
                        <p className="font-medium text-gray-900 dark:text-white">{hashResult.timestamp.toLocaleTimeString()}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Hash Verification */}
              {settings.enableComparison && hashResult && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Password Verification
                  </h2>
                  
                  <div className="space-y-4">
                    {/* Password Verification */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Verify Password
                      </label>
                      <div className="relative">
                        <input
                          type="password"
                          value={comparePassword}
                          onChange={(e) => setComparePassword(e.target.value)}
                          placeholder="Enter password to verify against hash..."
                          className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                          aria-label="Enter password for verification"
                        />
                      </div>
                    </div>

                    {/* Hash Comparison */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Compare with Expected Hash
                      </label>
                      <textarea
                        value={compareHash}
                        onChange={(e) => setCompareHash(e.target.value)}
                        placeholder="Enter expected scrypt hash to compare..."
                        rows={3}
                        className="w-full p-3 font-mono text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                        aria-label="Enter hash for comparison"
                      />
                    </div>

                    {/* Verification Results */}
                    {(comparePassword.trim() || compareHash.trim()) && (
                      <div className="space-y-2">
                        {comparePassword.trim() && (
                          <div className={`p-3 rounded-lg border ${
                            verifyPassword() 
                              ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
                              : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                          }`}>
                            <div className="flex items-center space-x-2">
                              {verifyPassword() ? (
                                <>
                                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                                  <span className="text-green-800 dark:text-green-300 font-medium">
                                    Hashes match! Password security verified.
                                  </span>
                                </>
                              ) : (
                                <>
                                  <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                                  <span className="text-red-800 dark:text-red-300 font-medium">
                                    Hashes do not match! Different passwords or parameters.
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Settings Panel */}
            {showSettings && (
              <div className="lg:col-span-1">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 sticky top-4">
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Scrypt Parameters
                  </h2>
                  
                  <div className="space-y-4">
                    {/* N Parameter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        N Parameter (CPU/Memory Cost): {settings.N}
                      </label>
                      <select
                        value={settings.N}
                        onChange={(e) => setSettings(prev => ({ ...prev, N: parseInt(e.target.value) }))}
                        className="w-full p-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:text-white"
                        aria-label="Select N parameter"
                      >
                        <option value={1024}>1024 (Very Fast)</option>
                        <option value={2048}>2048 (Fast)</option>
                        <option value={4096}>4096 (Moderate)</option>
                        <option value={8192}>8192 (Secure)</option>
                        <option value={16384}>16384 (Very Secure)</option>
                        <option value={32768}>32768 (Maximum)</option>
                      </select>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Higher N = More memory usage & security
                      </p>
                    </div>

                    {/* r Parameter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        r Parameter (Block Size): {settings.r}
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="16"
                        value={settings.r}
                        onChange={(e) => setSettings(prev => ({ ...prev, r: parseInt(e.target.value) }))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                        aria-label="Adjust r parameter"
                      />
                      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                        <span>1</span>
                        <span>16</span>
                      </div>
                    </div>

                    {/* p Parameter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        p Parameter (Parallelization): {settings.p}
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="8"
                        value={settings.p}
                        onChange={(e) => setSettings(prev => ({ ...prev, p: parseInt(e.target.value) }))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                        aria-label="Adjust p parameter"
                      />
                      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                        <span>1</span>
                        <span>8</span>
                      </div>
                    </div>

                    {/* Key Length */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Key Length: {settings.keyLength} bytes
                      </label>
                      <select
                        value={settings.keyLength}
                        onChange={(e) => setSettings(prev => ({ ...prev, keyLength: parseInt(e.target.value) }))}
                        className="w-full p-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:text-white"
                        aria-label="Select key length"
                      >
                        <option value={16}>16 bytes (128-bit)</option>
                        <option value={32}>32 bytes (256-bit)</option>
                        <option value={64}>64 bytes (512-bit)</option>
                      </select>
                    </div>

                    {/* Input Format */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Input Format
                      </label>
                      <select
                        value={settings.inputFormat}
                        onChange={(e) => setSettings(prev => ({ ...prev, inputFormat: e.target.value as InputFormat }))}
                        className="w-full p-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:text-white"
                        aria-label="Select input format"
                      >
                        <option value="text">Plain Text</option>
                        <option value="file">File Upload</option>
                        <option value="hex">Hexadecimal</option>
                        <option value="base64">Base64</option>
                        <option value="binary">Binary</option>
                        <option value="url">URL Encoded</option>
                      </select>
                    </div>

                    {/* Output Format */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Output Format
                      </label>
                      <select
                        value={settings.outputFormat}
                        onChange={(e) => setSettings(prev => ({ ...prev, outputFormat: e.target.value as OutputFormat }))}
                        className="w-full p-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:text-white"
                        aria-label="Select output format"
                      >
                        <option value="hex">Hexadecimal</option>
                        <option value="base64">Base64</option>
                        <option value="binary">Binary</option>
                      </select>
                    </div>

                    {/* Custom Salt */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Custom Salt (Optional)
                      </label>
                      <input
                        type="text"
                        value={settings.customSalt}
                        onChange={(e) => setSettings(prev => ({ ...prev, customSalt: e.target.value }))}
                        placeholder="Leave empty for auto-generated salt..."
                        className="w-full p-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:text-white"
                        aria-label="Enter custom salt"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Random salt improves security
                      </p>
                    </div>

                    {/* Security Options */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Security Options</h4>
                      {[
                        { key: 'memoryHardMode', label: 'Memory-Hard Mode', desc: 'Optimize for ASIC resistance' },
                        { key: 'authenticationMode', label: 'Authentication Mode', desc: 'Optimize for login systems' },
                        { key: 'passwordStrengthMode', label: 'Password Strength', desc: 'Analyze password security' },
                        { key: 'complianceMode', label: 'Compliance Mode', desc: 'Enterprise compliance features' },
                        { key: 'enableComparison', label: 'Hash Verification', desc: 'Enable password verification' },
                        { key: 'showAnalysis', label: 'Security Analysis', desc: 'Show detailed security metrics' },
                        { key: 'showEnterpriseMode', label: 'Enterprise Mode', desc: 'Enable enterprise features' },
                        { key: 'caseSensitive', label: 'Case Sensitive', desc: 'Consider letter case in input' },
                        { key: 'includeTimestamp', label: 'Include Timestamp', desc: 'Add timestamp for uniqueness' },
                        { key: 'showSecurityInfo', label: 'Security Information', desc: 'Show security recommendations' }
                      ].map(({ key, label, desc }) => (
                        <div key={key}>
                          <label className="flex items-start space-x-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={settings[key as keyof ScryptSettings] as boolean}
                              onChange={(e) => setSettings(prev => ({ ...prev, [key]: e.target.checked }))}
                              className="w-4 h-4 mt-0.5 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500"
                              aria-label={`Toggle ${label}`}
                            />
                            <div>
                              <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                              <p className="text-xs text-gray-500 dark:text-gray-400">{desc}</p>
                            </div>
                          </label>
                        </div>
                      ))}
                    </div>

                    {/* Memory Usage Estimate */}
                    <div className="p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                      <div className="flex items-start space-x-2">
                        {/* <Storag className="w-4 h-4 text-purple-600 dark:text-purple-400 mt-0.5" /> */}
                        <div>
                          <p className="text-sm font-medium text-purple-800 dark:text-purple-300">
                            Estimated Memory Usage: {formatBytes(settings.N * settings.r * 128)}
                          </p>
                          <p className="text-xs text-purple-700 dark:text-purple-400 mt-1">
                            Higher memory usage provides better ASIC resistance and security against hardware attacks.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Security Notice */}
                    {settings.showSecurityInfo && (
                      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <CheckCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                              Memory-Hard Security with Scrypt
                            </p>
                            <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
                              Scrypt provides memory-hard properties that make it expensive to implement in specialized hardware, promoting decentralization and security.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Analytics Panel */}
            {showAnalytics && (
              <div className="lg:col-span-1">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 sticky top-4">
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Performance Analytics
                  </h2>
                  
                  <div className="space-y-4">
                    {/* Quick Stats */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                        <div className="text-xl font-bold text-purple-600 dark:text-purple-400">
                          {analytics.totalHashes}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Total Hashes
                        </div>
                      </div>
                      <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        <div className="text-xl font-bold text-green-600 dark:text-green-400">
                          {analytics.averagePasswordStrength.toFixed(1)}%
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Avg Strength
                        </div>
                      </div>
                      <div className="text-center p-3 bg-pink-50 dark:bg-pink-900/20 rounded-lg">
                        <div className="text-xl font-bold text-pink-600 dark:text-pink-400">
                          {formatBytes(analytics.averageMemoryUsage)}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Avg Memory
                        </div>
                      </div>
                      <div className="text-center p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                        <div className="text-xl font-bold text-amber-600 dark:text-amber-400">
                          {analytics.complianceScore}%
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Compliance
                        </div>
                      </div>
                    </div>

                    {/* Parameter Distribution */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Parameter Usage
                      </h4>
                      <div className="space-y-2">
                        {Object.entries(analytics.parameterDistribution)
                          .sort(([, a], [, b]) => b - a)
                          .slice(0, 5)
                          .map(([params, count]) => (
                          <div key={params} className="flex justify-between items-center text-sm">
                            <span className="text-gray-600 dark:text-gray-400 font-mono text-xs">{params}</span>
                            <span className="font-medium text-gray-800 dark:text-white">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Security Level Distribution */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Security Levels
                      </h4>
                      <div className="space-y-2">
                        {analytics.hashHistory.length > 0 && (
                          (() => {
                            const levelCounts = analytics.hashHistory.reduce((acc, entry) => {
                              acc[entry.securityLevel] = (acc[entry.securityLevel] || 0) + 1;
                              return acc;
                            }, {} as Record<SecurityLevel, number>);

                            return Object.entries(levelCounts).map(([level, count]) => (
                              <div key={level} className="flex justify-between items-center text-sm">
                                <span className="text-gray-600 dark:text-gray-400 capitalize">{level}</span>
                                <span className="font-medium text-gray-800 dark:text-white">{count}</span>
                              </div>
                            ));
                          })()
                        )}
                      </div>
                    </div>

                    {/* Feature Usage */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Feature Usage
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Memory-Hard Mode</span>
                          <span className="font-medium text-gray-800 dark:text-white">{analytics.memoryHardModeUsage}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Authentication Mode</span>
                          <span className="font-medium text-gray-800 dark:text-white">{analytics.authenticationModeUsage}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Password Strength</span>
                          <span className="font-medium text-gray-800 dark:text-white">{analytics.passwordStrengthUsage}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Enterprise Mode</span>
                          <span className="font-medium text-gray-800 dark:text-white">{analytics.enterpriseModeUsage}</span>
                        </div>
                      </div>
                    </div>

                    {/* Performance Metrics */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Performance Metrics
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Average N</span>
                          <span className="font-medium text-gray-800 dark:text-white">{analytics.averageN}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Total Processing</span>
                          <span className="font-medium text-gray-800 dark:text-white">{analytics.totalProcessingTime.toFixed(1)}ms</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Security Score</span>
                          <span className="font-medium text-gray-800 dark:text-white">{analytics.securityScore}%</span>
                        </div>
                      </div>
                    </div>

                    {/* Recent Activity */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Recent Activity
                      </h4>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {analytics.hashHistory.slice(0, 5).map((entry, index) => (
                          <div key={index} className="text-xs text-gray-600 dark:text-gray-400 p-2 bg-gray-50 dark:bg-gray-700 rounded">
                            <div className="flex justify-between">
                              <span>N={entry.N}</span>
                              <span className="capitalize">{entry.securityLevel}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>{formatBytes(entry.memoryUsage)}</span>
                              <span>{entry.timestamp.toLocaleTimeString()}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Scrypt Information */}
                    <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <Info className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Scrypt Specifications
                        </span>
                      </div>
                      <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                        <li>• Memory-hard password hashing function</li>
                        <li>• ASIC-resistant design for decentralization</li>
                        <li>• Used in Litecoin, Dogecoin mining</li>
                        <li>• Configurable N, r, p parameters</li>
                        <li>• Superior resistance to hardware attacks</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Hidden SEO Content */}
          <div className="sr-only" aria-hidden="true">
            <h2>Advanced Scrypt Password Hash Generator for Cryptocurrency & Blockchain</h2>
            <p>
              Generate secure scrypt password hashes for cryptocurrency mining, blockchain 
              authentication, and memory-hard password storage. Our comprehensive scrypt 
              calculator provides ASIC-resistant password security with configurable N, r, p 
              parameters, enterprise-grade features for blockchain developers, cryptocurrency 
              engineers, and authentication systems.
            </p>
            
            <h3>Cryptocurrency and Blockchain Scrypt Features</h3>
            <ul>
              <li>Memory-hard scrypt password hashing with ASIC resistance</li>
              <li>Configurable N, r, p parameters for optimal security-performance balance</li>
              <li>Cryptocurrency mining simulation and blockchain authentication</li>
              <li>Litecoin and Dogecoin compatible hash generation</li>
              <li>Password strength analysis and security recommendations</li>
              <li>Enterprise-grade compliance and reporting features</li>
              <li>Blockchain development integration and authentication tools</li>
              <li>Memory usage optimization for hardware efficiency</li>
            </ul>
            
            <h3>Cryptocurrency Mining and Blockchain Applications</h3>
            <ul>
              <li>Cryptocurrency mining algorithm simulation and testing</li>
              <li>Blockchain authentication and wallet security</li>
              <li>Smart contract password verification systems</li>
              <li>Decentralized application (dApp) authentication</li>
              <li>Cryptocurrency exchange security infrastructure</li>
              <li>Mining pool authentication and worker verification</li>
              <li>Blockchain node security and network authentication</li>
              <li>Cryptocurrency wallet encryption and key derivation</li>
            </ul>
            
            <h3>ASIC Resistance and Memory-Hard Security</h3>
            <p>
              Scrypt's memory-hard properties provide superior resistance to ASIC-based 
              attacks by requiring significant memory bandwidth. This design promotes 
              mining decentralization and makes hardware-based password cracking 
              economically unfeasible, ensuring long-term security for cryptocurrency 
              and authentication applications.
            </p>
            
            <h3>Blockchain Development and Cryptocurrency Standards</h3>
            <p>
              Scrypt is widely adopted in major cryptocurrencies including Litecoin and 
              Dogecoin, making it essential for blockchain developers working on 
              cryptocurrency projects. Its proven track record in production environments 
              and resistance to specialized hardware attacks makes it ideal for 
              decentralized systems.
            </p>
            
            <h3>Memory Usage and Performance Optimization</h3>
            <p>
              Scrypt's configurable parameters allow developers to balance security 
              requirements with memory usage and processing time. The N parameter 
              controls memory cost, r affects block size, and p enables parallelization, 
              providing fine-grained control over resource usage in cryptocurrency 
              mining and authentication applications.
            </p>
            
            <h3>Enterprise Cryptocurrency Security</h3>
            <p>
              Enterprise cryptocurrency applications benefit from Scrypt's memory-hard 
              properties and proven security model. Its resistance to hardware attacks 
              and configurable parameters make it suitable for high-security 
              environments including cryptocurrency exchanges, institutional trading 
              platforms, and enterprise blockchain infrastructure.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScryptHashGenerator;